import { CSSProperties } from "@material-ui/core/styles/withStyles";

import { layoutTokens, semanticColors as colors, textStyles } from "./designTokens";

export type ButtonTone = "primary" | "trust" | "outline" | "text" | "inverse";
export type ButtonSize = "L" | "M" | "S";
export type LegacyButtonVariant =
  | "filled"
  | "filledForApprove"
  | "filledForAgentSearch"
  | "filledForAgentSearchFit"
  | "filledForProfileDetail"
  | "filledGray"
  | "filledGrayForLater"
  | "filledOrangeForMsins"
  | "filledWhite"
  | "filledWhiteTextPrimary"
  | "outlinedPrimary"
  | "outlinedGray"
  | "outlinedGrayForPendingOffer"
  | "textPrimary";

export const legacyButtonTones: Record<LegacyButtonVariant, ButtonTone> = {
  filled: "primary",
  filledForApprove: "trust",
  filledForAgentSearch: "primary",
  filledForAgentSearchFit: "primary",
  filledForProfileDetail: "primary",
  filledGray: "text",
  filledGrayForLater: "text",
  filledOrangeForMsins: "trust",
  filledWhite: "inverse",
  filledWhiteTextPrimary: "inverse",
  outlinedPrimary: "outline",
  outlinedGray: "outline",
  outlinedGrayForPendingOffer: "outline",
  textPrimary: "text",
};

export const resolveButtonSize = (variant?: LegacyButtonVariant, size?: "small" | "medium" | "large"): ButtonSize => {
  if (size === "small") return "M"; // 旧smallは40px。v2のSへ縮めない。
  if (size === "large") return "L";
  if (
    !variant ||
    [
      "filledForApprove",
      "filledForAgentSearch",
      "filledForAgentSearchFit",
      "filledForProfileDetail",
      "filledGray",
      "filledGrayForLater",
      "textPrimary",
    ].includes(variant)
  )
    return "M";
  return "L";
};

const filledStyle = (
  start: string,
  end: string,
  hover: string,
  pressedStart: string,
  pressedEnd: string
): CSSProperties => ({
  color: colors["text/inverse"],
  border: "1px solid transparent",
  backgroundColor: start,
  backgroundImage: `linear-gradient(90deg, ${start}, ${end})`,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: hover,
    backgroundImage: `linear-gradient(90deg, ${hover}, ${pressedEnd})`,
    boxShadow: "none",
  },
  "&:active": {
    backgroundColor: pressedStart,
    backgroundImage: `linear-gradient(90deg, ${pressedStart}, ${pressedEnd})`,
    boxShadow: "none",
  },
  "&.Mui-disabled, &.Mui-disabled:hover": {
    backgroundColor: colors["action/primary-disabled"],
    backgroundImage: "none",
    color: colors["text/inverse"],
    boxShadow: "none",
  },
});

export const buttonToneStyles: Record<ButtonTone, CSSProperties> = {
  primary: filledStyle(
    colors["action/primary"],
    colors["action/primary-grad-end"],
    colors["action/primary-hover"],
    colors["action/primary-hover"],
    colors["action/primary-pressed"]
  ),
  trust: filledStyle(
    colors["action/trust-grad-start"],
    colors["action/trust"],
    colors["action/trust"],
    colors["action/trust"],
    colors["action/trust-pressed-grad-end"]
  ),
  outline: {
    backgroundColor: colors["surface/card"],
    backgroundImage: "none",
    color: colors["action/primary"],
    border: `1px solid ${colors["action/primary"]}`,
    "&:hover": {
      backgroundColor: colors["surface/card"],
      color: colors["action/primary-hover"],
      borderColor: colors["action/primary-hover"],
    },
    "&:active": { color: colors["action/primary-pressed"], borderColor: colors["action/primary-pressed"] },
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: colors["surface/card"],
      backgroundImage: "none",
      borderColor: colors["border/strong"],
      color: colors["text/disabled"],
    },
  },
  text: {
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: colors["text/secondary"],
    border: "1px solid transparent",
    "&:hover, &:active": { backgroundColor: colors["state/text-pressed-bg"], color: colors["text/primary"] },
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "transparent",
      backgroundImage: "none",
      color: colors["text/disabled"],
    },
  },
  inverse: {
    backgroundColor: colors["surface/card"],
    backgroundImage: "none",
    color: colors["action/primary"],
    border: "1px solid transparent",
    "&:hover": { backgroundColor: colors["surface/base"], color: colors["action/primary-hover"] },
    "&:active": { backgroundColor: colors["surface/base"], color: colors["action/primary-pressed"] },
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: colors["surface/card"],
      backgroundImage: "none",
      color: colors["text/disabled"],
    },
  },
};

export const buttonBaseStyle: CSSProperties = {
  borderRadius: layoutTokens.radius.full,
  fontWeight: 500,
  lineHeight: 1.3,
  letterSpacing: 0,
  textTransform: "none",
  boxShadow: "none",
  "&:hover, &:active": { boxShadow: "none" },
  "& .MuiTypography-root": { fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit" },
};

export const buttonSizeStyles: Record<ButtonSize, CSSProperties> = {
  L: {
    ...textStyles["PC/Button/L"],
    height: 48,
    padding: "0 24px",
    [`@media (max-width:${layoutTokens.breakpoints.md - 0.05}px)`]: { fontSize: textStyles["SP/Button/L"].fontSize },
  },
  M: { ...textStyles["PC/Button/M"], height: 40, padding: "0 20px" },
  S: { ...textStyles["PC/Button/S"], height: 32, padding: "0 16px" },
};
